import { Box, Button, Link } from '@mui/material';
import React, { useContext } from 'react';
import { useState } from 'react';
import { AppContext } from '../../AppContext';
import {
  IFtseFile,
  IFtseType,
  useCreateFtseFileMutation,
  useGetFtseTypesQuery,
  useUpdateFtseMutation
} from '../../generated/graphql';
import { sameFtseFileNameExists } from '../../utils/clientUtils';
import { Actions } from '../../enums/ActionEnums';
import { Edit } from '@mui/icons-material';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { Colors, buttons } from '../../utils/theme';
import DialogBox from '../Common/Layout/DialogBox';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import InputComponent from '../Common/Fields/InputComponent';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, ftseFileInput?: IFtseFile) => void;
  ftseFileInput: IFtseFile | null;
};

export type IErrors = {
  name: string | null;
  location_on_server: string | null;
  file_type: string | null;
};

const defaultErrorsObj: IErrors = {
  name: null,
  location_on_server: null,
  file_type: null
};

const errorTexts = {
  name: 'Name is required',
  location_on_server: 'Server path is required',
  file_type: 'File type is required',
  existingName: 'File with that name already exists'
};

const ftseFileForm: React.FunctionComponent<IProps> = ({ handleClose, ftseFileInput }: IProps) => {
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editable, setEditable] = useState(ftseFileInput === null);
  const [name, setName] = useState(ftseFileInput?.name || null);
  const [locationOnServer, setLocationOnServer] = useState(ftseFileInput?.file_location || null);
  const [fileType, setFileType] = useState(ftseFileInput?.type || null);
  const { dispatch } = useContext(AppContext);
  const fileTypeOptions = useGetFtseTypesQuery();
  const [createFtseFile, createFtseFileResult] = useCreateFtseFileMutation();
  const [updateFtseFile, updateFtseFileResult] = useUpdateFtseMutation();

  const handleCancel = () => {
    if (editable && ftseFileInput !== null) {
      setName(ftseFileInput?.name);
      setLocationOnServer(ftseFileInput?.file_location);
      setFileType(ftseFileInput?.type || null);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      file_type: null,
      location_on_server: null,
      name: null
    };
    if (!name) {
      hasErrors = true;
      errors.name = errorTexts.name;
    }
    if (!fileType) {
      hasErrors = true;
      errors.file_type = errorTexts.file_type;
    }
    if (!locationOnServer) {
      hasErrors = true;
      errors.location_on_server = errorTexts.location_on_server;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const nameExists = sameFtseFileNameExists(name as string, ftseFileInput?.id);
      if (nameExists) {
        setFormErrors({ ...formErrors, name: nameExists ? errorTexts.existingName : null });
      } else {
        handleSave(ftseFileInput === null ? 'create' : 'update');
      }
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createFtseFile({
        variables: {
          input: {
            name: name as string,
            file_type_id: Number(fileType?.id),
            location_on_server: locationOnServer as string
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IFtseFile);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Ftse file created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateFtseFile({
        variables: {
          id: ftseFileInput?.id as string,
          input: {
            name: name as string,
            file_type_id: Number(fileType?.id),
            location_on_server: locationOnServer as string
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IFtseFile);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Ftse file updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  const handleFtseFileNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, name: null });
    } else {
      setFormErrors({ ...formErrors, name: errorTexts.name });
    }
    setName(value);
  };

  const handleFileTypeChange = (_: unknown, newValue: React.SetStateAction<IFtseType | null>) => {
    setFormErrors({ ...formErrors, file_type: newValue === null ? errorTexts.file_type : null });
    setFileType(newValue);
  };

  const handleFtseFileLocationChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value) {
      setFormErrors({ ...formErrors, location_on_server: null });
    } else {
      setFormErrors({ ...formErrors, location_on_server: errorTexts.location_on_server });
    }
    setLocationOnServer(value);
  };

  return (
    <DialogBox
      id="ftse-form-dialog-title"
      width={689}
      title={ftseFileInput ? `${ftseFileInput.name} Details` : 'Create FTSE file'}
    >
      {ftseFileInput && (
        <Box sx={{ textAlign: 'right', marginRight: '15px', marginTop: '10px' }}>
          <Button
            id="ftse-form-edit-button"
            data-testid="ftse-form-edit-button"
            component={Link}
            onClick={() => setEditable(true)}
            disabled={editable}
            sx={{ ...buttons.edit, fontWeight: 'bold' }}
          >
            <Edit
              sx={{
                fontSize: 'medium',
                color: editable ? Colors.gray : Colors.skyBlue,
                marginRight: '3'
              }}
            />
            EDIT
          </Button>
        </Box>
      )}
      <Box sx={{ m: '30px', mt: '60px', display: 'flex', flexDirection: 'column', gap: '50px' }}>
        <InputComponent
          id="ftse-form-name"
          label="Title/Name of file"
          value={name || ''}
          onChange={handleFtseFileNameChange}
          errorMsg={formErrors.name}
          disabled={!editable || createFtseFileResult.loading || updateFtseFileResult.loading}
        />
        <InputComponent
          id="ftse-form-file-location"
          label="Location on server"
          value={locationOnServer || ''}
          onChange={handleFtseFileLocationChange}
          errorMsg={formErrors.location_on_server}
          disabled={!editable || createFtseFileResult.loading || updateFtseFileResult.loading}
        />
        <AutocompleteComponent
          id="ftse-type-autocomplete"
          label="File Type"
          options={(fileTypeOptions.data?.result as never[]) || []}
          disabled={createFtseFileResult.loading || updateFtseFileResult.loading || !editable}
          values={fileType}
          onChange={handleFileTypeChange}
          getOptionLabel={(option: IFtseType) => option.name}
          errorMsg={formErrors.file_type}
        />
        <Box className="centralize" sx={{ marginTop: '20px' }}>
          <CancelButton
            id="cancel-ftse-file-button"
            data-testid="cancel-ftse-file-button"
            disabled={createFtseFileResult.loading || updateFtseFileResult.loading}
            onClick={handleCancel}
            text={ftseFileInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-ftse-file-button"
            data-testid="save-ftse-file-button"
            disabled={!editable || createFtseFileResult.loading || updateFtseFileResult.loading}
            onClick={handleSubmit}
            text={ftseFileInput === null ? 'Create' : 'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default ftseFileForm;
